'use client';

import { useUserWallets, Wallet } from '@dynamic-labs/sdk-react-core';
import { XMarkIcon } from '@heroicons/react/20/solid';
import Image from 'next/image';
import { useState } from 'react';

import { GET_ASSETS_FOR_USER } from '@repo/api-core-toolkit';
import { Loader, tw } from '@repo/ui';
import {
  getImageUrl,
  roundToTwoSignificantDigitsWithMinDecimals,
  shortenEthAddress,
  useDatabaseQuery,
  useScreenSize,
} from '@repo/utils';

import { useSwitchWalletCore } from '@claim/hooks/use-switch-wallet-core';

import { useCompressedBalance } from '../../../../../hooks/use-compressed-balance';
import { useUnlinkWallet } from '../../../../../hooks/use-unlink-wallet';

interface ConnectedWalletProps {
  wallet: Wallet;
}

export const ConnectedWallet: React.FC<ConnectedWalletProps> = ({ wallet }) => {
  const { isMobile } = useScreenSize();
  const userWallets = useUserWallets();

  const { handleUnlinkWallet } = useUnlinkWallet();
  const { handleSwitchWallet } = useSwitchWalletCore();
  const { data: compressedBalance, isLoading: isLoadingCompressedBalance } =
    useCompressedBalance(wallet);

  const {
    data: assetsForUser,
    isLoading,
    isFetching,
  } = useDatabaseQuery({
    queryKey: ['assets for user', userWallets.map((wallet) => wallet.address)],
    query: GET_ASSETS_FOR_USER as any,
    variables: {},
    field: 'getAssetsForUser',
    enabled: true,
    headers: {
      'api-key': process.env.NEXT_PUBLIC_GRAPHQL_API_KEY as string,
    },
  });

  const handleClick = () => {
    setIsUnlinking(true);
    handleUnlinkWallet(wallet);
  };

  const [isUnlinking, setIsUnlinking] = useState(false);
  const loadingForWallet =
    isLoading || isFetching || isLoadingCompressedBalance;

  const amountForWallet = compressedBalance
    ? compressedBalance
    : (assetsForUser?.totalAssetsPerWallet?.[wallet.address.toLowerCase()] ??
      0);

  const hasUnclaimedDood = amountForWallet > 0 && !loadingForWallet;
  const formattedAmount =
    roundToTwoSignificantDigitsWithMinDecimals(
      amountForWallet,
    ).toLocaleString();

  const shouldShowUnlinkButton =
    userWallets.length > 1 &&
    wallet.key !== 'turnkeyhd' &&
    wallet.key !== 'dynamicwaas';

  return (
    <div className='flex flex-col'>
      <div
        className={tw(
          'flex items-start justify-between rounded-xl bg-white/10 p-2',
        )}
      >
        <div className='flex items-start gap-4 md:gap-2'>
          <div
            className={tw(
              'relative h-[30px] w-[30px] rounded-full',
              wallet.chain !== 'SOL' && 'bg-black',
            )}
          >
            <Image
              fill
              src={getImageUrl(
                `/claim/images/${wallet.chain === 'SOL' ? 'solana' : 'eth'}.png`,
              )}
              alt='eth logo'
              sizes='(max-width: 640px) 30px, 40px'
              className={tw(wallet.chain !== 'SOL' && 'p-1')}
            />
          </div>
          <div className='flex flex-col'>
            <p
              className={tw(
                'text-xs font-semibold tracking-[1.6px] md:mt-0.5 md:text-base',
                'flex flex-col',
              )}
            >
              {shortenEthAddress(wallet.address, 6, 6)}{' '}
              {wallet.key === 'turnkeyhd' || wallet.key === 'dynamicwaas' ? (
                <span className='mb-0.5 text-xs font-thin text-white/50 md:-mt-1 md:mb-0 md:text-[13px]'>
                  (Stoodio Wallet)
                </span>
              ) : null}
            </p>

            {isMobile ? (
              <p
                className={tw(
                  'text-xs tracking-[1.3px] md:pr-5',
                  hasUnclaimedDood
                    ? 'text-[#05FFA9]'
                    : !loadingForWallet
                      ? 'text-white/50'
                      : 'animate-pulse text-white/50',
                )}
              >
                {!loadingForWallet
                  ? `${formattedAmount} $DOOD WAITING`
                  : 'loading...'}
              </p>
            ) : null}
          </div>
        </div>

        {!isMobile ? (
          <div className='mt-0.5 flex items-center justify-center gap-2'>
            <p
              className={tw(
                'font-semibold tracking-[1.3px]',
                hasUnclaimedDood
                  ? 'text-[#05FFA9]'
                  : !loadingForWallet
                    ? 'text-white/50'
                    : 'animate-pulse text-white/50',
              )}
            >
              {!loadingForWallet ? (
                <>
                  <span className='mr-[3px]'>{formattedAmount}</span> $DOOD
                  WAITING
                </>
              ) : (
                'loading...'
              )}
            </p>

            {/* DESKTOP */}
            {!loadingForWallet && shouldShowUnlinkButton ? (
              !isUnlinking ? (
                <XMarkIcon
                  onClick={handleClick}
                  className={tw(
                    'mt-0.5 block h-5 w-5 stroke-[3px] opacity-50 hover:cursor-pointer',
                  )}
                />
              ) : (
                <div className={tw('mt-0.5 block opacity-50')}>
                  <Loader
                    size='small'
                    theme='secondary'
                  />
                </div>
              )
            ) : null}
          </div>
        ) : null}

        {/* MOBILE */}
        {!loadingForWallet && shouldShowUnlinkButton ? (
          !isUnlinking ? (
            <XMarkIcon
              onClick={handleClick}
              className={tw('block h-6 w-6 stroke-[3px] opacity-50 md:hidden')}
            />
          ) : (
            <div className={tw('block opacity-50 md:hidden')}>
              <Loader
                size='small'
                theme='secondary'
              />
            </div>
          )
        ) : null}
      </div>

      {compressedBalance ? (
        <div className='px-6'>
          <div className='rounded-b-lg bg-white/5 px-8 py-3 text-center text-[13px] text-white/50'>
            You have {formattedAmount} $DOOD tokens waiting in this wallet.
            Before your $DOOD is usable, you'll need to{' '}
            <span
              onClick={() => handleSwitchWallet(wallet)}
              className='underline hover:cursor-pointer'
            >
              decompress it.
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
};
